/**
 * Privacy.tsx
 *
 * File-level: Privacy Policy page that presents Edicoo's privacy practices.
 */

import React from 'react'
import { Link } from 'react-router'
import { Button } from '../components/ui/button'

/**
 * Privacy component
 *
 * Renders the Privacy Policy content with clear sections and a simple layout.
 *
 * @returns {JSX.Element}
 */
export default function Privacy(): JSX.Element {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm p-8">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last Updated: 25.11.2025</p>
        </header>

        <article className="prose prose-neutral max-w-none text-gray-700">
          <p>
            Edicoo Advertising &amp; Media Agency (“Edicoo”) respects your privacy and is committed to protecting your
            personal information.
          </p>

          <h2>1. Information We May Collect</h2>
          <p>We may collect:</p>
          <ul>
            <li>Contact details (name, email, phone)</li>
            <li>Company information</li>
            <li>Website usage data (IP, browser type, pages visited)</li>
            <li>Project-related files you voluntarily provide</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>We use your data to:</p>
          <ul>
            <li>Respond to inquiries</li>
            <li>Deliver media and digital marketing services</li>
            <li>Improve our website and performance</li>
            <li>Send project updates and essential communication</li>
          </ul>
          <p>We do not sell personal data.</p>

          <h2>3. Data Sharing</h2>
          <p>
            We may share data only with trusted service providers necessary to deliver campaigns (media vendors, digital
            platforms, analytics tools). We may also share information if legally required.
          </p>

          <h2>4. Data Security</h2>
          <p>We use reasonable technical and organizational measures to protect your data, but no system is completely secure.</p>

          <h2>5. Cookies</h2>
          <p>Our website may use cookies for analytics and website performance. You may disable cookies in your browser settings.</p>

          <h2>6. Your Rights</h2>
          <p>
            You may request access, correction, or deletion of your data by contacting us at{' '}
            <a href="mailto:info@edicoo.com" className="text-orange-600 hover:underline">info@edicoo.com</a>
          </p>

          <h2>7. Updates</h2>
          <p>We may update this Privacy Policy periodically. Continued use of our website means you accept any revisions.</p>
        </article>

        <div className="mt-8 flex items-center justify-between">
          <Button asChild variant="outline" className="bg-transparent">
            <Link to="/">Back to Home</Link>
          </Button>

          <Button asChild size="sm" className="bg-orange-600 text-white hover:bg-orange-500">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}